// src/pages/NewHomeThree.jsx
// Piano With Pan — Landing Page V5 "Game Trailer"
// Visual-first. Minimal words. Show, don't tell.

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import VirtualPiano, {
  normalizeNoteName as vpNormalizeNoteName,
} from "../components/VirtualPiano";

const NAV_LOGO = "/icons/pwp-192.png";
const PAN = "/images/pan-the-panda.png";
const WIS = "/images/wis.png";
const DORON_SLEEP = "/images/sleeping-doron.png";
const TREBLE_BG = "/images/PanWisHead.png";

/* ─── NAV ─── */
function Nav({ nav }) {
  const { session, loading, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const has = Boolean(session);

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);

  const login = () => nav(`/login?back=${encodeURIComponent(window.location.pathname)}`);
  const logout = async () => {
    setBusy(true);
    try { await signOut(); } catch {}
    finally { setBusy(false); nav("/home", { replace: true }); }
  };

  return (
    <header className={`nh3-nav ${scrolled ? "nh3-nav--scrolled" : ""}`}>
      <div className="nh3-nav-inner">
        <button onClick={() => nav("/home")} className="nh3-logo-btn">
          <img src={NAV_LOGO} alt="" className="nh3-logo-img"
            onError={e => { e.currentTarget.style.display = "none"; }} />
          <span className="nh3-logo-text">Piano With Pan</span>
        </button>
        <div className="nh3-nav-right">
          <div className="nh3-nav-desktop">
            <button onClick={() => nav("/parents")} className="nh3-nav-link">For Parents</button>
            <button onClick={() => nav("/pwp-paywall")} className="nh3-nav-pill">Full Access</button>
          </div>
          {!loading && has ? (
            <button onClick={logout} disabled={busy} className="nh3-nav-out">{busy ? "..." : "Log Out"}</button>
          ) : (
            <button onClick={login} className="nh3-nav-login">Log In</button>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─── HERO ─── */
function Hero({ onStart }) {
  const [playedNotes, setPlayedNotes] = useState([]);
  const [wrongFlash, setWrongFlash] = useState(false);
  const wrongFlashTimeoutRef = useRef(null);
  
  const targetSequence = useMemo(() => ["C", "C", "G", "G", "A", "A", "G"], []);
  const melodyDisplay = useMemo(() => [
    { notes: ["C", "C"], lyrics: "Twin-kle" },
    { notes: ["G", "G"], lyrics: "twin-kle" },
    { notes: ["A", "A", "G"], lyrics: "lit-tle star" },
  ], []);

  const normalizeNote = useCallback((note) => {
    if (!note) return "";
    return vpNormalizeNoteName ? vpNormalizeNoteName(String(note).trim().toUpperCase()) : String(note).trim().toUpperCase();
  }, []);

  const handleNoteClick = useCallback((noteName) => {
    const note = normalizeNote(noteName);
    const newPlayed = [...playedNotes, note];
    
    // Check if this matches the expected note
    const expectedNote = targetSequence[playedNotes.length];
    if (note !== normalizeNote(expectedNote)) {
      // Wrong note - show error
      setWrongFlash(true);
      if (wrongFlashTimeoutRef.current) clearTimeout(wrongFlashTimeoutRef.current);
      wrongFlashTimeoutRef.current = setTimeout(() => {
        setWrongFlash(false);
        wrongFlashTimeoutRef.current = null;
      }, 300);
      return;
    }
    
    // Correct note
    setPlayedNotes(newPlayed);
  }, [playedNotes, targetSequence, normalizeNote]);

  const resetGame = useCallback(() => {
    setPlayedNotes([]);
    setWrongFlash(false);
  }, []);

  const isComplete = playedNotes.length === targetSequence.length;
  const progress = playedNotes.length;

  useEffect(() => {
    return () => {
      if (wrongFlashTimeoutRef.current) {
        clearTimeout(wrongFlashTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className="nh3-hero">
      {/* Treble Town BG */}
      <div className="nh3-hero-bg">
        <img src={TREBLE_BG} alt="" className="nh3-hero-bg-img" />
        <div className="nh3-hero-bg-overlay" />
      </div>

      <div className="nh3-hero-content">
        <h1 className="nh3-hero-h1">
          Piano lessons your child will <span className="nh3-hero-highlight">actually enjoy</span>
        </h1>

        <p className="nh3-hero-sub">
          Piano With Pan turns practice into play with real songs, real progress and real fun with a piano adventure that ensures daily piano practice habits for children.
        </p>

        {/* Interactive Demo */}
        <div className="nh3-demo-box">
          {isComplete ? (
            <div className="nh3-demo-complete">
              <div className="nh3-demo-complete-icon">🎉</div>
              <div className="nh3-demo-complete-title">Perfect! That's Twinkle Twinkle!</div>
              <button onClick={onStart} className="nh3-cta-big">
                Start Learning Now →
              </button>
              <button onClick={resetGame} className="nh3-demo-reset">
                ↺ Try Again
              </button>
            </div>
          ) : (
            <>
              <div className="nh3-demo-header">
                <div className="nh3-demo-title">🎵 Try playing this melody</div>
                <div className="nh3-demo-subtitle">Click the keys below in order</div>
              </div>

              {/* Melody Board - Just like Level 1 */}
              <div className="nh3-melody-board">
                <div className="nh3-melody-groups">
                  {melodyDisplay.map((group, groupIdx) => {
                    const groupStart = melodyDisplay.slice(0, groupIdx).reduce((sum, g) => sum + g.notes.length, 0);
                    
                    return (
                      <div key={groupIdx} className="nh3-melody-group">
                        <div className="nh3-melody-notes">
                          {group.notes.map((note, noteIdx) => {
                            const globalIdx = groupStart + noteIdx;
                            const isPlayed = globalIdx < progress;
                            const isCurrent = globalIdx === progress;
                            const isWrong = isCurrent && wrongFlash;
                            
                            return (
                              <div
                                key={`${groupIdx}-${noteIdx}`}
                                className={`nh3-note-box ${isPlayed ? "nh3-note-played" : ""} ${isCurrent ? "nh3-note-current" : ""} ${isWrong ? "nh3-note-wrong" : ""}`}
                              >
                                {note}
                              </div>
                            );
                          })}
                        </div>
                        <div className="nh3-melody-lyrics">{group.lyrics}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Progress bar */}
                <div className="nh3-melody-progress">
                  <div className="nh3-melody-progress-bar">
                    <div 
                      className="nh3-melody-progress-fill"
                      style={{ width: `${(progress / targetSequence.length) * 100}%` }}
                    />
                  </div>
                  <div className="nh3-melody-progress-text">
                    {progress}/{targetSequence.length} notes
                  </div>
                </div>
              </div>

              {/* Virtual Piano */}
              <div className="nh3-piano-container">
                <div className="nh3-piano-wrapper">
                  <VirtualPiano
                    onNoteClick={handleNoteClick}
                    keyWidth={48}
                    whiteKeyHeight={140}
                    blackKeyWidth={28}
                    blackKeyHeight={90}
                    firstKey="C4"
                    lastKey="C5"
                    showAllBlackLabels={false}
                    blackKeyLabels={{}}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {!isComplete && (
          <button onClick={onStart} className="nh3-cta-secondary">
            Skip to Full Experience →
          </button>
        )}
      </div>

      {/* Scroll hint */}
      <div className="nh3-scroll-hint">
        <span className="nh3-scroll-arrow">↓</span>
      </div>
    </section>
  );
}

/* ─── HOW IT WORKS — 4 step blocks ─── */
function HowSection() {
  const items = [
    { icon: "📖", line1: "Learn concept", line2: "master the basics" },
    { icon: "🎹", line1: "Practice it", line2: "build muscle memory" },
    { icon: "🎵", line1: "Play the song", line2: "complete the task" },
    { icon: "⭐", line1: "Collect stars", line2: "earn your reward" },
  ];
  return (
    <section className="nh3-how">
      <h2 className="nh3-section-h2">How it works</h2>
      <div className="nh3-how-grid">
        {items.map(i => (
          <div key={i.line1} className="nh3-how-card">
            <span className="nh3-how-icon">{i.icon}</span>
            <span className="nh3-how-line1">{i.line1}</span>
            <span className="nh3-how-line2">{i.line2}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── TRUST SIGNALS ─── */
function TrustSection() {
  return (
    <section className="nh3-trust">
      <div className="nh3-trust-inner">
        <div className="nh3-trust-item">
          <span className="nh3-trust-icon">🎵</span>
          <span className="nh3-trust-label">Learn 10 fun piano songs</span>
        </div>
        <div className="nh3-trust-item">
          <span className="nh3-trust-icon">🎓</span>
          <span className="nh3-trust-label">Created by music educator Nishat Chadha</span>
        </div>
        <div className="nh3-trust-item">
          <span className="nh3-trust-icon">📚</span>
          <span className="nh3-trust-label">Structured 10-level curriculum</span>
        </div>
        <div className="nh3-trust-item">
          <span className="nh3-trust-icon">⭐</span>
          <span className="nh3-trust-label">Start free — no credit card required</span>
        </div>
      </div>
    </section>
  );
}

/* ─── THE JOURNEY — visual level path ─── */
function JourneySection() {
  const levels = [
    { n: 1, name: "The Sleep Spell", song: "Twinkle Twinkle", e: "🐉" },
    { n: 2, name: "Zeb's Birthday", song: "Happy Birthday", e: "🦓" },
    { n: 3, name: "Peng's Melting Ice", song: "", e: "🐧" },
    { n: 4, name: "Doron Awakens", song: "", e: "🔥" },
    { n: 5, name: "Ren's Network", song: "", e: "🦊" },
    { n: 10, name: "Ancient Melody", song: "Final boss", e: "🏆" },
  ];
  return (
    <section className="nh3-journey">
      <h2 className="nh3-section-h2 nh3-white">The journey</h2>
      <p className="nh3-journey-sub">10 levels. 10 real songs. 1 trophy.</p>
      <div className="nh3-journey-path">
        {levels.map((l, i) => (
          <div key={l.n} className={`nh3-level-node ${i === 0 ? "nh3-level-active" : ""}`}>
            <div className="nh3-level-circle">{l.e}</div>
            <div className="nh3-level-info">
              <span className="nh3-level-tag">Level {l.n}</span>
              <span className="nh3-level-name">{l.name}</span>
              {l.song && <span className="nh3-level-song">🎵 {l.song}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── FOR PARENTS — bullet grid ─── */
function ParentsSection() {
  const points = [
    { icon: "🎯", text: "Structured curriculum — not random YouTube videos" },
    { icon: "📊", text: "Track your child's progress through 10 progressive levels" },
    { icon: "🎹", text: "No piano? No problem — virtual keyboard included" },
    { icon: "⏱️", text: "Just 10–15 minutes a day builds lasting piano habits" },
    { icon: "🎓", text: "Learn music theory through real songs, not boring drills" },
    { icon: "💰", text: "Fraction of the cost of traditional piano lessons" },
  ];
  return (
    <section className="nh3-parents">
      <h2 className="nh3-section-h2">Why parents choose Piano With Pan</h2>
      <p className="nh3-parents-sub">The structure of lessons, the fun of a game</p>
      <div className="nh3-parents-grid">
        {points.map(p => (
          <div key={p.text} className="nh3-parent-item">
            <span className="nh3-parent-icon">{p.icon}</span>
            <span className="nh3-parent-text">{p.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── QUOTES ─── */
function QuoteSection() {
  return (
    <section className="nh3-quote-section">
      <blockquote className="nh3-quote">
        "My 6-year-old played Happy Birthday for grandma after just 2 weeks. I couldn't believe it!"
      </blockquote>
      <cite className="nh3-cite">— Priya M., parent of 6-year-old</cite>
      
      <blockquote className="nh3-quote nh3-quote-second">
        "Finally, screen time that actually teaches something. Now my son reminds ME about piano practice!"
      </blockquote>
      <cite className="nh3-cite">— Rahul S., parent of 8-year-old</cite>
    </section>
  );
}

/* ─── FREE vs FULL ─── */
function PricingSection() {
  return (
    <section className="nh3-pricing">
      <h2 className="nh3-section-h2">Try it free, upgrade when ready</h2>
      <p className="nh3-pricing-sub">No credit card required to start</p>
      <div className="nh3-pricing-grid">
        <div className="nh3-price-card nh3-price-free">
          <div className="nh3-price-badge nh3-badge-green">Free Forever</div>
          <ul className="nh3-price-list">
            <li>Levels 1–3: 3 songs — free forever, no time limit</li>
            <li>Learn Your Notes — unlimited</li>
            <li>Practice Mode — unlimited</li>
            <li>Progress saved across devices</li>
          </ul>
        </div>
        <div className="nh3-price-card nh3-price-full">
          <div className="nh3-price-badge nh3-badge-purple">Full Game Access</div>
          <ul className="nh3-price-list">
            <li>Everything in Free (Levels 1–3 unlimited)</li>
            <li>Levels 4–10 unlocked — 7 more songs</li>
            <li>All 10 songs + complete the full quest</li>
            <li>Final battle with Doron + all trophies</li>
          </ul>
          <div className="nh3-price-note">₹299 for full game access — fraction of traditional lessons</div>
        </div>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCTA({ onStart }) {
  return (
    <section className="nh3-final">
      <div className="nh3-final-inner">
        <div className="nh3-final-chars">
          <img src={WIS} alt="" className="nh3-final-char-sm" onError={e=>{e.currentTarget.style.display="none";}} />
          <img src={PAN} alt="" className="nh3-final-char-lg" onError={e=>{e.currentTarget.style.display="none";}} />
          <img src={DORON_SLEEP} alt="" className="nh3-final-char-sm" onError={e=>{e.currentTarget.style.display="none";}} />
        </div>
        <h2 className="nh3-final-h2">Give your child the gift of music</h2>
        <p className="nh3-final-msg">Start free today. No credit card. No risk. Just piano.</p>
        <button onClick={onStart} className="nh3-cta-big">Start Free Today →</button>
        <p className="nh3-final-note">✓ Works on any device · ✓ Ages 5+ · ✓ Cancel anytime</p>
      </div>
    </section>
  );
}

/* ─── FLOATING CTA ─── */
function FloatCTA({ show, onClick }) {
  if (!show) return null;
  return (
    <div className="nh3-float-cta">
      <button onClick={onClick} className="nh3-cta-big nh3-cta-sm">Get Started →</button>
    </div>
  );
}

/* ─── FOOTER ─── */
function Footer({ nav }) {
  return (
    <footer className="nh3-footer">
      <span className="nh3-footer-brand">Piano With Pan™</span>
      <div className="nh3-footer-links">
        <button onClick={() => nav("/parents")}>For Parents</button>
        <button onClick={() => nav("/pwp-paywall")}>Full Access</button>
      </div>
      <span className="nh3-footer-copy">© {new Date().getFullYear()} · Nishat Chadha · Mumbai 🇮🇳</span>
    </footer>
  );
}

/* ─── MAIN ─── */
export default function NewHomeThree() {
  const nav = useNavigate();
  const [showFloat, setShowFloat] = useState(false);

  useEffect(() => {
    const f = () => setShowFloat(window.scrollY > 500);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);

  const go = useCallback(() => nav("/game-menu"), [nav]);

  return (
    <div className="nh3-root">
      <style>{CSS}</style>
      <Nav nav={nav} />
      <FloatCTA show={showFloat} onClick={go} />
      <main>
        <Hero onStart={go} />
        <TrustSection />
        <HowSection />
        <JourneySection />
        <ParentsSection />
        <QuoteSection />
        <PricingSection />
        <FinalCTA onStart={go} />
      </main>
      <Footer nav={nav} />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@700;800&family=Quicksand:wght@600;700&display=swap');

/* ── Reset & Root ── */
.nh3-root {
  --yellow: #fbbf24;
  --purple: #7c3aed;
  --dark: #0f172a;
  --light: #fefce8;
  min-height: 100vh;
  background: var(--dark);
  color: #fff;
  font-family: 'Quicksand', sans-serif;
  font-weight: 600;
  overflow-x: hidden;
}

/* ── Nav ── */
.nh3-nav {
  position: sticky; top: 0; z-index: 100;
  background: rgba(15,23,42,0.92);
  backdrop-filter: blur(16px);
  border-bottom: 2px solid transparent;
  transition: all 0.3s;
}
.nh3-nav--scrolled {
  background: rgba(15,23,42,0.97);
  border-color: var(--yellow);
}
.nh3-nav-inner {
  max-width: 1400px; margin: 0 auto;
  display: flex; align-items: center;
  padding: 10px 20px; gap: 12px;
}
.nh3-logo-btn {
  display: flex; align-items: center; gap: 10px;
  background: none; border: none; cursor: pointer; color: #fff;
}
.nh3-logo-img { width: 32px; height: 32px; border-radius: 8px; }
.nh3-logo-text {
  font-family: 'Baloo 2', cursive; font-size: 1.05rem; font-weight: 800;
}
.nh3-nav-right {
  margin-left: auto; display: flex; align-items: center; gap: 10px;
}
.nh3-nav-desktop { display: none; align-items: center; gap: 10px; }
@media(min-width:768px) { .nh3-nav-desktop { display: flex; } }
.nh3-nav-link {
  background: none; border: none; color: #94a3b8; cursor: pointer;
  font-weight: 700; font-size: 0.8rem; transition: color 0.2s;
}
.nh3-nav-link:hover { color: #fff; }
.nh3-nav-pill {
  background: var(--purple); color: #fff; border: none; cursor: pointer;
  padding: 5px 12px; border-radius: 16px; font-weight: 800; font-size: 0.75rem;
}
.nh3-nav-login {
  background: var(--yellow); color: var(--dark); border: none; cursor: pointer;
  padding: 5px 14px; border-radius: 16px; font-weight: 800; font-size: 0.75rem;
}
.nh3-nav-out {
  background: none; border: 2px solid #ef4444; color: #ef4444; cursor: pointer;
  padding: 4px 12px; border-radius: 16px; font-weight: 800; font-size: 0.75rem;
}

/* ── Hero ── */
.nh3-hero {
  position: relative;
  min-height: 100vh; min-height: 100svh;
  display: flex; align-items: center; justify-content: center;
  text-align: center;
  overflow: hidden;
}
.nh3-hero-bg {
  position: absolute; inset: 0; z-index: 0;
}
.nh3-hero-bg-img {
  width: 100%; height: 100%; object-fit: cover;
  opacity: 1;
}
.nh3-hero-bg-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.55) 60%, rgba(255,255,255,0.75) 100%);
}
.nh3-hero-content {
  position: relative; z-index: 2;
  padding: 50px 16px 30px;
  max-width: 800px;
}

@media(min-width:640px) {
  .nh3-hero-content {
    padding: 60px 20px 30px;
  }
}

.nh3-hero-h1 {
  font-family: 'Baloo 2', cursive;
  font-size: clamp(1.75rem, 5.5vw, 3.2rem);
  font-weight: 800; line-height: 1.2;
  color: #0a0e1a;
  margin: 0 0 12px;
  text-shadow: 0 2px 16px rgba(255,255,255,0.95), 0 4px 24px rgba(255,255,255,0.8);
  padding: 0 8px;
}
.nh3-hero-highlight {
  color: #5b21b6;
  position: relative;
}
.nh3-hero-highlight::after {
  content: '';
  position: absolute; left: -4px; right: -4px; bottom: 2px;
  height: 14px; background: var(--yellow); opacity: 0.45;
  border-radius: 4px; z-index: -1;
  transform: rotate(-1deg);
}
.nh3-hero-sub {
  font-size: 0.875rem; color: #1e293b; font-weight: 700;
  margin: 0 0 16px; letter-spacing: 0.01em;
  line-height: 1.5;
  text-shadow: 0 2px 12px rgba(255,255,255,0.95), 0 4px 20px rgba(255,255,255,0.75);
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 12px;
}
@media(min-width:640px) { .nh3-hero-sub { font-size: 1.1rem; padding: 0 20px; } }

/* ── Demo Box (Interactive Piano Demo) ── */
.nh3-demo-box {
  margin-top: 16px;
  background: white;
  border-radius: 20px;
  padding: 16px 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  border: 3px solid #0a0e1a;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

@media(min-width:640px) {
  .nh3-demo-box {
    padding: 20px 16px;
    border-radius: 24px;
  }
}

.nh3-demo-header {
  text-align: center;
  margin-bottom: 16px;
}

.nh3-demo-title {
  font-family: 'Baloo 2', cursive;
  font-size: 1.05rem;
  font-weight: 800;
  color: #0a0e1a;
  margin-bottom: 4px;
}

@media(min-width:640px) {
  .nh3-demo-title {
    font-size: 1.2rem;
  }
}

.nh3-demo-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 600;
}

/* Melody Board - matching Level 1 */
.nh3-melody-board {
  margin-bottom: 16px;
}

.nh3-melody-groups {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.nh3-melody-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.nh3-melody-notes {
  display: flex;
  gap: 6px;
}

.nh3-note-box {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Baloo 2', cursive;
  font-size: 1.1rem;
  font-weight: 800;
  color: #475569;
  transition: all 0.2s;
}

.nh3-note-played {
  background: #6ee7b7;
  border-color: #059669;
  color: #064e3b;
}

.nh3-note-current {
  background: white;
  border: 3px solid #4f46e5;
  color: #4f46e5;
  animation: nh3notePulse 1.5s ease-in-out infinite;
}

.nh3-note-wrong {
  background: #fca5a5;
  border-color: #dc2626;
  color: #991b1b;
  animation: nh3shake 0.3s ease;
}

@keyframes nh3notePulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.nh3-melody-lyrics {
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
  text-align: center;
}

.nh3-melody-progress {
  text-align: center;
}

.nh3-melody-progress-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
  border: 2px solid #cbd5e1;
}

.nh3-melody-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4f46e5, #7c3aed);
  border-radius: 8px;
  transition: width 0.3s ease;
}

.nh3-melody-progress-text {
  font-size: 0.8rem;
  font-weight: 700;
  color: #64748b;
}

/* Piano Container */
.nh3-piano-container {
  margin-top: 14px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
}

.nh3-piano-wrapper {
  display: flex;
  justify-content: center;
  transform: scale(0.95);
  transform-origin: center;
}

@media(max-width: 640px) {
  .nh3-piano-wrapper {
    transform: scale(0.7);
  }
  .nh3-note-box {
    width: 38px;
    height: 38px;
    font-size: 0.9rem;
  }
  .nh3-melody-lyrics {
    font-size: 0.72rem;
  }
}

@media(max-width: 380px) {
  .nh3-piano-wrapper {
    transform: scale(0.6);
  }
  .nh3-note-box {
    width: 36px;
    height: 36px;
    font-size: 0.85rem;
  }
  .nh3-demo-box {
    padding: 14px 10px;
  }
}

/* Complete State */
.nh3-demo-complete {
  text-align: center;
  padding: 20px;
}

.nh3-demo-complete-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.nh3-demo-complete-title {
  font-family: 'Baloo 2', cursive;
  font-size: 1.5rem;
  font-weight: 800;
  color: #16a34a;
  margin-bottom: 20px;
}

.nh3-demo-reset {
  margin-top: 12px;
  padding: 10px 24px;
  border-radius: 12px;
  background: white;
  border: 2px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
}

.nh3-demo-reset:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.nh3-cta-secondary {
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 14px;
  background: white;
  color: #0a0e1a;
  border: 2px solid #cbd5e1;
  font-family: 'Quicksand', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}

.nh3-cta-secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

@keyframes nh3shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-8px); }
  75% { transform: translateX(8px); }
}

/* ── Big CTA ── */
.nh3-cta-big {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 14px 32px; border-radius: 16px;
  background: var(--yellow); color: var(--dark);
  border: 3px solid var(--dark);
  font-family: 'Baloo 2', cursive; font-size: 1rem; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.04em;
  box-shadow: 0 6px 0 0 #92400e;
  cursor: pointer; transition: all 0.15s;
}
.nh3-cta-big:hover { transform: translateY(-3px); box-shadow: 0 9px 0 0 #92400e; }
.nh3-cta-big:active { transform: translateY(2px); box-shadow: 0 2px 0 0 #92400e; }
.nh3-cta-sm { padding: 12px 28px; font-size: 0.95rem; }

@media(min-width:640px) {
  .nh3-cta-big {
    padding: 18px 40px;
    font-size: 1.15rem;
    border-radius: 18px;
  }
}

/* Scroll hint */
.nh3-scroll-hint {
  position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%);
  z-index: 2;
}
.nh3-scroll-arrow {
  display: block; font-size: 1.5rem; color: var(--dark);
  animation: nh3bounce 2s infinite;
  opacity: 0.5;
}
@keyframes nh3bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(8px)} }

/* ── Trust Signals ── */
.nh3-trust {
  background: #fff;
  padding: 32px 20px;
  border-bottom: 2px solid #e2e8f0;
}
.nh3-trust-inner {
  max-width: 1000px; margin: 0 auto;
  display: flex; align-items: center; justify-content: center;
  gap: 32px; flex-wrap: wrap;
}
.nh3-trust-item {
  display: flex; align-items: center; gap: 8px;
}
.nh3-trust-icon { font-size: 1.2rem; }
.nh3-trust-label {
  font-size: 0.85rem; font-weight: 700; color: #475569;
}
@media(max-width:768px) {
  .nh3-trust-inner { flex-direction: column; gap: 16px; text-align: center; }
  .nh3-trust-item { flex-direction: column; gap: 4px; }
}

/* ── How It Works ── */
.nh3-how {
  background: var(--light);
  color: var(--dark);
  padding: 64px 20px;
  text-align: center;
}
.nh3-section-h2 {
  font-family: 'Baloo 2', cursive;
  font-size: clamp(1.6rem, 4vw, 2.4rem);
  font-weight: 800; margin: 0 0 32px;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.nh3-white { color: #fff; }
.nh3-how-grid {
  display: grid; grid-template-columns: repeat(4,1fr);
  gap: 16px; max-width: 900px; margin: 0 auto;
}
@media(max-width:768px) { .nh3-how-grid { grid-template-columns: repeat(2,1fr); } }
@media(max-width:500px) { .nh3-how-grid { grid-template-columns: 1fr; max-width: 320px; } }
.nh3-how-card {
  background: #fff; border: 3px solid var(--dark);
  border-radius: 20px; padding: 28px 16px;
  box-shadow: 5px 5px 0 0 var(--dark);
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  transition: transform 0.2s;
}
.nh3-how-card:hover { transform: translateY(-4px); }
.nh3-how-icon { font-size: 2.5rem; }
.nh3-how-line1 {
  font-family: 'Baloo 2', cursive; font-size: 1.1rem;
  font-weight: 800; color: var(--dark);
}
.nh3-how-line2 { font-size: 0.82rem; color: #64748b; font-weight: 700; }

/* ── Journey ── */
.nh3-journey {
  background: linear-gradient(135deg, var(--purple), #4c1d95);
  padding: 64px 20px; text-align: center;
}
.nh3-journey-sub {
  color: #c4b5fd; font-size: 1rem; font-weight: 700;
  margin: -20px 0 36px;
}
.nh3-journey-path {
  display: flex; flex-direction: column; gap: 0;
  max-width: 400px; margin: 0 auto;
  position: relative;
}
/* Connector line */
.nh3-journey-path::before {
  content: '';
  position: absolute; left: 27px; top: 24px; bottom: 24px;
  width: 4px; background: rgba(255,255,255,0.15);
  border-radius: 2px;
}
.nh3-level-node {
  display: flex; align-items: center; gap: 16px;
  padding: 12px 0; position: relative; z-index: 1;
}
.nh3-level-circle {
  width: 56px; height: 56px; border-radius: 50%;
  background: rgba(255,255,255,0.1);
  border: 3px solid rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; flex-shrink: 0;
}
.nh3-level-active .nh3-level-circle {
  background: var(--yellow);
  border-color: var(--yellow);
  box-shadow: 0 0 20px rgba(251,191,36,0.4);
}
.nh3-level-info {
  display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
}
.nh3-level-tag {
  font-size: 0.65rem; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.1em; color: #c4b5fd;
}
.nh3-level-name {
  font-family: 'Baloo 2', cursive; font-size: 1.05rem; font-weight: 800;
  color: #fff;
}
.nh3-level-song { font-size: 0.75rem; color: var(--yellow); font-weight: 700; }
.nh3-level-active .nh3-level-tag { color: var(--yellow); }

/* ── Parents ── */
.nh3-parents {
  background: var(--light); color: var(--dark);
  padding: 64px 20px; text-align: center;
}
.nh3-parents-sub {
  color: #64748b; font-size: 1rem; font-weight: 700;
  margin: -20px 0 32px;
}
.nh3-parents-grid {
  display: grid; grid-template-columns: repeat(2,1fr);
  gap: 12px; max-width: 700px; margin: 0 auto;
}
@media(max-width:600px) { .nh3-parents-grid { grid-template-columns: 1fr; } }
.nh3-parent-item {
  display: flex; align-items: center; gap: 12px;
  background: #fff; border: 2px solid #e2e8f0;
  border-radius: 14px; padding: 14px 16px;
  text-align: left;
}
.nh3-parent-icon { font-size: 1.4rem; flex-shrink: 0; }
.nh3-parent-text { font-size: 0.88rem; font-weight: 700; color: var(--dark); }

/* ── Quote ── */
.nh3-quote-section {
  background: var(--dark); padding: 56px 20px; text-align: center;
}
.nh3-quote {
  font-family: 'Baloo 2', cursive;
  font-size: clamp(1.3rem, 3.5vw, 2rem);
  color: var(--yellow); font-weight: 800;
  max-width: 600px; margin: 0 auto 12px;
  line-height: 1.3;
}
.nh3-quote-second {
  margin-top: 32px;
}
.nh3-cite { font-size: 0.85rem; color: #64748b; font-style: normal; font-weight: 700; }

/* ── Pricing ── */
.nh3-pricing {
  background: linear-gradient(180deg, var(--dark), #1a1a2e);
  padding: 64px 20px; text-align: center;
}
.nh3-pricing-sub {
  color: #94a3b8; font-size: 0.95rem; font-weight: 700;
  margin: -20px 0 32px;
}
.nh3-pricing-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 16px; max-width: 640px; margin: 0 auto;
}
@media(max-width:600px) { .nh3-pricing-grid { grid-template-columns: 1fr; } }
.nh3-price-card {
  border-radius: 20px; padding: 28px 20px; text-align: left;
}
.nh3-price-free {
  background: rgba(255,255,255,0.05); border: 2px solid #334155;
}
.nh3-price-full {
  background: rgba(124,58,237,0.15); border: 2px solid var(--purple);
  position: relative;
}
.nh3-price-badge {
  display: inline-block; padding: 4px 12px; border-radius: 20px;
  font-size: 0.72rem; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.08em; margin-bottom: 14px;
}
.nh3-badge-green { background: #16a34a; color: #fff; }
.nh3-badge-purple { background: var(--purple); color: #fff; }
.nh3-price-list {
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 10px;
}
.nh3-price-list li {
  font-size: 0.85rem; font-weight: 700; color: #cbd5e1;
  padding-left: 20px; position: relative;
}
.nh3-price-list li::before {
  content: '✓'; position: absolute; left: 0; color: var(--yellow); font-weight: 800;
}
.nh3-price-note {
  margin-top: 16px; padding-top: 16px;
  border-top: 1px solid rgba(255,255,255,0.1);
  font-size: 0.75rem; font-weight: 700; color: #a78bfa;
}

/* ── Final CTA ── */
.nh3-final {
  background: var(--yellow);
  padding: 64px 20px; text-align: center;
}
.nh3-final-inner { max-width: 500px; margin: 0 auto; }
.nh3-final-chars {
  display: flex; align-items: flex-end; justify-content: center;
  gap: 8px; margin-bottom: 24px;
}
.nh3-final-char-sm { width: 48px; height: 48px; object-fit: contain; }
.nh3-final-char-lg { width: 72px; height: 72px; object-fit: contain; }
@media(min-width:640px) {
  .nh3-final-char-sm { width: 64px; height: 64px; }
  .nh3-final-char-lg { width: 96px; height: 96px; }
}
.nh3-final-h2 {
  font-family: 'Baloo 2', cursive;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800; color: var(--dark);
  margin: 0 0 12px;
}
.nh3-final-msg {
  font-size: 1.1rem; color: #78350f; font-weight: 700;
  margin: 0 0 24px;
}
.nh3-final-note {
  margin-top: 16px; font-size: 0.85rem; color: #78350f; font-weight: 700;
}

/* ── Float CTA ── */
.nh3-float-cta {
  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
  z-index: 90;
  animation: nh3slideIn 0.3s ease-out;
}
@keyframes nh3slideIn {
  from { transform: translateX(-50%) translateY(60px); opacity: 0; }
  to { transform: translateX(-50%) translateY(0); opacity: 1; }
}

/* ── Footer ── */
.nh3-footer {
  background: #0a0e1a; padding: 32px 20px;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  text-align: center;
}
.nh3-footer-brand {
  font-family: 'Baloo 2', cursive; font-size: 1.1rem; color: #fff; font-weight: 800;
}
.nh3-footer-links { display: flex; gap: 16px; }
.nh3-footer-links button {
  background: none; border: none; color: #64748b; cursor: pointer;
  font-size: 0.8rem; font-weight: 700;
}
.nh3-footer-links button:hover { color: #fff; }
.nh3-footer-copy { font-size: 0.72rem; color: #475569; }
`;